import type {
  IntegrationCrmAccessSettings,
  IntegrationItem,
  IntegrationProvider,
} from '@/api/integrations/integrations.types'
import rolesEnum from '@/constant/rolesEnum'
import { getBaseRole } from '@/helpers/customRoles'
import { readMaxCrmSettings } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/MaxCrmBotsSettings/lib/maxCrmIntegration.helpers'
import { readTgCrmSettings } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/TgCrmBotsSettings/lib/tgCrmIntegration.helpers'
import { readVkCrmSettings } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/VkCrmCommunitiesSettings/lib/vkCrmIntegration.helpers'
import { readWazzupCrmSettings } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import { VK_LEAD_CHAT_ALLOWED_ROLES } from './vkIntegrationLeadChat'

export const CRM_INTEGRATION_ACCESS_ROLE_SLUGS = [
  'admin',
  'junior-admin',
  'head-of-sales',
  'sales-manager',
] as const

/** Пустой access с бэка / «без ограничений» (fallback на дефолтные роли CRM). */
export const EMPTY_CRM_INTEGRATION_ACCESS: IntegrationCrmAccessSettings = {
  roles: [],
  departments: [],
}

/** Стартовые права при подключении новой CRM-интеграции. */
export const createDefaultCrmIntegrationAccess =
  (): IntegrationCrmAccessSettings => ({
    roles: [rolesEnum.admin],
    departments: [],
  })

type AccessUser = {
  role?: string | null
  roles?: string[] | null
  departments?: unknown
  departmentIds?: unknown
  units?: unknown
}

const getIds = (value: unknown, keys: string[] = ['id', 'value']): string[] => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.flatMap((item) => getIds(item, keys))
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)]
  }

  if (typeof value === 'object') {
    const item = value as Record<string, unknown>

    return keys
      .flatMap((key) => getIds(item[key]))
      .filter((id, index, array) => array.indexOf(id) === index)
  }

  return []
}

const hasIntersection = (
  accessIds: Array<number | string> | undefined,
  userIds: string[],
): boolean => {
  if (!accessIds?.length) return false

  const userIdSet = new Set(userIds.map(String))

  return accessIds.map(String).some((id) => userIdSet.has(id))
}

const getUserRoles = (user: AccessUser): string[] =>
  (user.role?.startsWith('custom_') ? [user.role] : [user.role, ...(Array.isArray(user.roles) ? user.roles : [])])
    .filter(Boolean)
    .map(String)

const getUserDepartmentIds = (user: AccessUser): string[] => [
  ...getIds(user.departments, ['id', 'department_id', 'value']),
  ...getIds(user.departmentIds),
  ...getIds(user.units, ['id', 'unit_id', 'value']),
]

export const hasCrmIntegrationAccessRestrictions = (
  access?: IntegrationCrmAccessSettings | null,
): boolean =>
  Boolean(access?.roles?.length || access?.departments?.length)

export const isUserAllowedByCrmIntegrationAccess = (
  access: IntegrationCrmAccessSettings | undefined,
  user: AccessUser | null | undefined,
): boolean => {
  if (!user) return false

  if (!hasCrmIntegrationAccessRestrictions(access)) {
    const userRoles = getUserRoles(user)
    return userRoles.some((role) =>
      (VK_LEAD_CHAT_ALLOWED_ROLES as readonly string[]).includes(getBaseRole(role)),
    )
  }

  const userRoles = getUserRoles(user)

  return (
    hasIntersection(access?.roles, userRoles) ||
    hasIntersection(access?.departments, getUserDepartmentIds(user))
  )
}

const readAccessByProvider: Record<
  IntegrationProvider,
  (item: IntegrationItem) => IntegrationCrmAccessSettings
> = {
  vkontakte: (item) => readVkCrmSettings(item).access ?? EMPTY_CRM_INTEGRATION_ACCESS,
  telegram: (item) => readTgCrmSettings(item).access ?? EMPTY_CRM_INTEGRATION_ACCESS,
  max: (item) => readMaxCrmSettings(item).access ?? EMPTY_CRM_INTEGRATION_ACCESS,
  wazzup: (item) =>
    readWazzupCrmSettings(item).access ?? EMPTY_CRM_INTEGRATION_ACCESS,
}

export const readIntegrationCrmAccess = (
  item: IntegrationItem,
): IntegrationCrmAccessSettings =>
  readAccessByProvider[item.provider]?.(item) ?? EMPTY_CRM_INTEGRATION_ACCESS

/** Доступ к чатам интеграции для текущего пользователя (поле `crm_access` с бэка). */
export const isIntegrationCrmAccessible = (item: IntegrationItem): boolean => {
  if (item.crm_access != null) {
    return Boolean(item.crm_access.is_accessible)
  }

  return true
}

export const buildIntegrationCrmAccessibilityMap = (
  integrations: IntegrationItem[],
): Record<number, boolean> =>
  Object.fromEntries(
    integrations.map((item) => [item.id, isIntegrationCrmAccessible(item)]),
  )

export const filterLeadProviderChatTabsByAccess = <
  T extends { integration_id?: number | string | null },
>(
  tabs: T[],
  connectedIntegrations: IntegrationItem[],
): T[] => {
  if (!tabs.length || !connectedIntegrations.length) {
    return tabs
  }

  const accessById = buildIntegrationCrmAccessibilityMap(connectedIntegrations)

  return tabs.filter((tab) => {
    const integrationId = Number(tab.integration_id)
    if (!Number.isFinite(integrationId) || integrationId <= 0) {
      return false
    }

    return accessById[integrationId] ?? false
  })
}

export type LeadProviderChatTabWithAccess<T> = T & { isAccessible: boolean }

export const mapLeadProviderChatTabsWithAccess = <
  T extends { integration_id?: number | string | null },
>(
  tabs: T[],
  connectedIntegrations: IntegrationItem[],
): LeadProviderChatTabWithAccess<T>[] => {
  if (!tabs.length) {
    return []
  }

  if (!connectedIntegrations.length) {
    return tabs.map((tab) => ({ ...tab, isAccessible: true }))
  }

  const accessById = buildIntegrationCrmAccessibilityMap(connectedIntegrations)

  return tabs.map((tab) => {
    const integrationId = Number(tab.integration_id)
    const isAccessible =
      Number.isFinite(integrationId) &&
      integrationId > 0 &&
      (accessById[integrationId] ?? false)

    return { ...tab, isAccessible }
  })
}

export const isLeadProviderConversationAccessible = (
  tabs: Array<{ conversation_id?: number | string | null; integration_id?: number | string | null }>,
  connectedIntegrations: IntegrationItem[],
  conversationId: number | null | undefined,
): boolean => {
  if (conversationId == null || !Number.isFinite(Number(conversationId))) {
    return true
  }

  const accessibleTabs = filterLeadProviderChatTabsByAccess(
    tabs,
    connectedIntegrations,
  )

  return accessibleTabs.some(
    (tab) => Number(tab.conversation_id) === Number(conversationId),
  )
}

/** Доступ к CRM-чатам: хотя бы одна подключённая интеграция доступна пользователю. */
export const isCrmIntegrationChatAllowedForUser = (
  user: AccessUser | null | undefined,
  connectedIntegrations: IntegrationItem[],
): boolean => {
  const chatIntegrations = connectedIntegrations.filter((item) => {
    if (item.crm_access != null) {
      return isIntegrationCrmAccessible(item)
    }

    const crm =
      item.provider === 'vkontakte'
        ? readVkCrmSettings(item)
        : item.provider === 'telegram'
          ? readTgCrmSettings(item)
          : item.provider === 'wazzup'
            ? readWazzupCrmSettings(item)
            : readMaxCrmSettings(item)

    if (crm.create_chat === false) return false

    return isUserAllowedByCrmIntegrationAccess(
      readIntegrationCrmAccess(item),
      user,
    )
  })

  if (chatIntegrations.length === 0) {
    const userRoles = user ? getUserRoles(user) : []
    return userRoles.some((role) =>
      (VK_LEAD_CHAT_ALLOWED_ROLES as readonly string[]).includes(getBaseRole(role)),
    )
  }

  return chatIntegrations.length > 0
}
